import { Check, X } from "lucide-react";

/**
 * PasswordStrengthMeter — OWASP-inspired visual feedback for password creation.
 * Shows a colored bar (5 levels) + inline checklist of the 5 rules.
 * Also exports `computeStrength()` so parent forms can gate submit.
 */
export function computeStrength(pw) {
  const rules = {
    length: !!pw && pw.length >= 8,
    lower: /[a-z]/.test(pw || ""),
    upper: /[A-Z]/.test(pw || ""),
    digit: /[0-9]/.test(pw || ""),
    symbol: /[^A-Za-z0-9]/.test(pw || ""),
  };
  const score = Object.values(rules).filter(Boolean).length; // 0..5
  const labels = ["Muito fraca", "Muito fraca", "Fraca", "Razoável", "Boa", "Forte"];
  return { score, label: labels[score], rules, isStrong: score === 5 };
}

const RULES = [
  { key: "length", label: "8+ caracteres" },
  { key: "lower", label: "letra minúscula" },
  { key: "upper", label: "letra maiúscula" },
  { key: "digit", label: "número" },
  { key: "symbol", label: "símbolo (!@#$…)" },
];

export function PasswordStrengthMeter({ password, showRules = true, testIdPrefix = "pw-strength" }) {
  const { score, label, rules } = computeStrength(password);
  const barColor =
    score <= 1 ? "bg-red-500"
    : score === 2 ? "bg-amber-500"
    : score === 3 ? "bg-yellow-500"
    : score === 4 ? "bg-emerald-500"
    : "bg-emerald-600";
  const barWidth = ["w-0", "w-1/5", "w-2/5", "w-3/5", "w-4/5", "w-full"][score];

  return (
    <div className="mt-1.5" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 bg-[#E8E6E1] rounded overflow-hidden">
          <div
            data-testid={`${testIdPrefix}-bar`}
            data-score={score}
            className={`h-full transition-all ${barColor} ${barWidth}`}
          />
        </div>
        <span
          data-testid={`${testIdPrefix}-label`}
          className="text-[10px] text-[#6B6B6B] font-mono w-16 text-right"
        >
          {password ? label : ""}
        </span>
      </div>
      {showRules && (
        <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid={`${testIdPrefix}-rules`}>
          {RULES.map((r) => {
            const ok = rules[r.key];
            return (
              <li
                key={r.key}
                data-testid={`${testIdPrefix}-rule-${r.key}`}
                data-ok={ok ? "true" : "false"}
                className={`flex items-center gap-1.5 text-[11px] ${ok ? "text-emerald-700" : "text-[#9B9B9B]"}`}
              >
                {ok ? <Check className="w-3 h-3 flex-shrink-0" /> : <X className="w-3 h-3 flex-shrink-0" />}
                <span>{r.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
